/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Insights", description = "Monitor ad performance, detect creative fatigue, analyze competitor ads, and act on real-time market signals — all from within Ad Studio.", lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Insights",
        description: "Monitor ad performance, detect creative fatigue, analyze competitor ads, and act on real-time market signals — all from within Ad Studio.",
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Monitor performance",
          "depth": 0,
          "id": "monitor-performance"
        }, {
          "value": "What it shows",
          "depth": 1,
          "id": "what-it-shows"
        }, {
          "value": "How to use it",
          "depth": 1,
          "id": "how-to-use-it"
        }, {
          "value": "Detect creative fatigue",
          "depth": 0,
          "id": "detect-creative-fatigue"
        }, {
          "value": "What it shows",
          "depth": 1,
          "id": "what-it-shows-1"
        }, {
          "value": "How to use it",
          "depth": 1,
          "id": "how-to-use-it-1"
        }, {
          "value": "Analyze competitor ads",
          "depth": 0,
          "id": "analyze-competitor-ads"
        }, {
          "value": "What it shows",
          "depth": 1,
          "id": "what-it-shows-2"
        }, {
          "value": "How to use it",
          "depth": 1,
          "id": "how-to-use-it-2"
        }, {
          "value": "Discover signals",
          "depth": 0,
          "id": "discover-signals"
        }, {
          "value": "What it shows",
          "depth": 1,
          "id": "what-it-shows-3"
        }, {
          "value": "Set up signals",
          "depth": 1,
          "id": "set-up-signals"
        }, {
          "value": "How to use it",
          "depth": 1,
          "id": "how-to-use-it-3"
        }, {
          "value": "Act on insights",
          "depth": 0,
          "id": "act-on-insights"
        }, {
          "value": "What's next",
          "depth": 0,
          "id": "whats-next"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      h3: "h3",
      ul: "ul",
      li: "li",
      ol: "ol",
      img: "img"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, creative teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsx("ul", {
                children: _jsxs("li", {
                  children: ["At least one connected ad platform in Context Hub's ", _jsx(_components.a, {
                    href: "/context-hub/connections#connect-advertising-and-lifecycle-platforms",
                    children: "Connected data"
                  }), " with performance data syncing"]
                })
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Insights shows how your ads are performing across your connected platforms. Monitor performance, identify stale creative, see what competitors are running, and discover real-time market signals. When you find an opportunity, create new ads directly from Insights."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Insights covers four areas: performance, fatigue, competition, and signals. Each area has its own tab within the Insights section."
      }), "\n", _jsx(_components.p, {
        children: "Insights connects analytics to ad creation. You can identify an opportunity and create new ads in response without switching between your ad platforms, a spreadsheet, and a separate creative tool."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "monitor-performance",
        children: "Monitor performance"
      }), "\n", _jsx(_components.p, {
        children: "The Performance tab shows ad performance metrics across your connected ad platforms. Use it to identify your top and bottom performers and decide where to invest creative effort."
      }), "\n", _jsx(_components.h3, {
        id: "what-it-shows",
        children: "What it shows"
      }), "\n", _jsx(_components.p, {
        children: "Performance aggregates metrics from your connected platforms into a single view. Common metrics include:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Spend"
          }), " — How much budget is allocated to each ad"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Impressions"
          }), " — How many times the ad was shown"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Clicks"
          }), " — How many clicks the ad received"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Conversions"
          }), " — How many conversions the ad drove"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "CPA"
          }), " — Cost per acquisition"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "CTR"
          }), " — Click-through rate"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "ROAS"
          }), " — Return on ad spend"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "how-to-use-it",
        children: "How to use it"
      }), "\n", _jsx(_components.p, {
        children: "Filter by platform, campaign, date range, or ad format to focus your analysis. Sort by any metric to find your best and worst performers. Keep comparisons like-for-like — static against static and video against video — since engagement rates differ by format."
      }), "\n", _jsx(_components.p, {
        children: "Use Performance data to inform your next creative move:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["A top-performing ad is a strong candidate for the ", _jsx(_components.strong, {
            children: "Upload an ad and create variants"
          }), " mode — generate more versions of what's working."]
        }), "\n", _jsxs(_components.li, {
          children: ["An underperforming ad may need a fresh approach — use ", _jsx(_components.strong, {
            children: "Upload a brief"
          }), " or ", _jsx(_components.strong, {
            children: "Ride a new trend"
          }), " to try a different creative direction."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "detect-creative-fatigue",
        children: "Detect creative fatigue"
      }), "\n", _jsx(_components.p, {
        children: "The Fatigue tab detects ads that are experiencing declining performance over time, indicating that your audience has seen them too many times."
      }), "\n", _jsx(_components.h3, {
        id: "what-it-shows-1",
        children: "What it shows"
      }), "\n", _jsx(_components.p, {
        children: "Fatigue shows ads where key metrics like CTR are declining or CPA is increasing over a sustained period, even if overall performance is still acceptable. This helps you refresh creative before performance degrades significantly."
      }), "\n", _jsx(_components.h3, {
        id: "how-to-use-it-1",
        children: "How to use it"
      }), "\n", _jsx(_components.p, {
        children: "Monitor fatigue signals regularly. When the Fatigue tab flags an ad:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Review the ad and its performance trend."
        }), "\n", _jsxs(_components.li, {
          children: ["Use the ", _jsx(_components.strong, {
            children: "Upload an ad and create variants"
          }), " mode to generate fresh versions that maintain the winning elements while introducing enough variation to re-engage your audience."]
        }), "\n", _jsxs(_components.li, {
          children: ["Alternatively, use ", _jsx(_components.strong, {
            children: "Upload a brief"
          }), " to take a completely new creative direction."]
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "The Fatigue tab updates as new performance data syncs. Check it weekly to catch declining creatives before they significantly impact campaign metrics."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "analyze-competitor-ads",
        children: "Analyze competitor ads"
      }), "\n", _jsx(_components.p, {
        children: "The Competitors tab provides visibility into competitor ad activity across platforms."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ad-studio/insights/as-competitors-hightouch.png",
          alt: "Competitor analysis"
        })
      }), "\n", _jsx(_components.h3, {
        id: "what-it-shows-2",
        children: "What it shows"
      }), "\n", _jsx(_components.p, {
        children: "Browse and analyze competitor ads to understand their messaging, creative approach, and campaign themes. The Competitors tab shows ads from competitors you're tracking, organized by platform and recency."
      }), "\n", _jsx(_components.h3, {
        id: "how-to-use-it-2",
        children: "How to use it"
      }), "\n", _jsx(_components.p, {
        children: "Use competitor intelligence to inform your creative strategy:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Identify messaging themes or visual approaches that are trending in your category."
        }), "\n", _jsx(_components.li, {
          children: "Spot gaps in competitor positioning that you can fill with your own creative."
        }), "\n", _jsxs(_components.li, {
          children: ["Use the ", _jsx(_components.strong, {
            children: "Riff on a competitor"
          }), " creation mode to generate your own on-brand response to a specific competitor ad directly from the Competitors tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Generate a ", _jsx(_components.strong, {
            children: "competitors report"
          }), " that summarizes trends, themes, and opportunities across your tracked competitors. Share the report with your team or use it as input for a creative brief."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "discover-signals",
        children: "Discover signals"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ad-studio/insights/as-insights-signals.png",
          alt: "Signals tab"
        })
      }), "\n", _jsx(_components.p, {
        children: "The Signals tab shows curated, real-time events happening in the world that are relevant to your business — recent news, social listening, competitive moves, and emerging trends. Each signal is a concise brief designed to inspire your next ad concept."
      }), "\n", _jsx(_components.h3, {
        id: "what-it-shows-3",
        children: "What it shows"
      }), "\n", _jsx(_components.p, {
        children: "Signals combines social listening, competitive analysis, and web search into a single feed tailored to your business context. Each signal includes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Headline"
          }), " — A summary of the event or trend"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "The opportunity"
          }), " — How the signal connects to your brand and what angle you could take"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Why now"
          }), " — Context on why this moment matters for your marketing"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Competitors"
          }), " — How competitors are positioned relative to the signal"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " — Cited articles and publications so you can verify and dig deeper"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ad-studio/insights/as-insights-signals-sources.png",
          alt: "Signal sources and actions"
        })
      }), "\n", _jsx(_components.h3, {
        id: "set-up-signals",
        children: "Set up signals"
      }), "\n", _jsxs(_components.p, {
        children: ["The first time you open Signals, the feed is empty. Select ", _jsx(_components.strong, {
          children: "Get signals"
        }), ", then ", _jsx(_components.strong, {
          children: "Continue"
        }), ", to tell Ad Studio where to research and what to research."]
      }), "\n", _jsx(_components.p, {
        children: "You choose two things:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " — Where the agent looks. Toggle on the suggested sources you want, such as open web research, Reddit, X (Twitter), TikTok, Instagram, and Facebook, or select ", _jsx(_components.strong, {
            children: "Add source"
          }), " to add your own."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Topics"
          }), " — What the agent looks for, grouped into ", _jsx(_components.strong, {
            children: "Industry"
          }), ", ", _jsx(_components.strong, {
            children: "My brand"
          }), ", ", _jsx(_components.strong, {
            children: "Competitors"
          }), ", and ", _jsx(_components.strong, {
            children: "Cultural"
          }), ". Edit the suggested topic chips, or select ", _jsx(_components.strong, {
            children: "Add topics"
          }), " to add your own."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The agent uses your sources and topics to build the feed, so more specific topics return more relevant signals."
      }), "\n", _jsxs(_components.p, {
        children: ["To change your sources and topics later, select the settings icon in the ", _jsx(_components.strong, {
          children: "Insights"
        }), " header, then select ", _jsx(_components.strong, {
          children: "Research sources"
        }), " or ", _jsx(_components.strong, {
          children: "Topics"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "how-to-use-it-3",
        children: "How to use it"
      }), "\n", _jsxs(_components.p, {
        children: ["Browse the signal list in the left panel and select any signal to read the full analysis. When you find a signal worth acting on, select ", _jsx(_components.strong, {
          children: "Create ad"
        }), " at the bottom of the signal detail. Ad Studio opens a new chat pre-filled with a brief based on the signal report, so you can generate ad concepts without leaving the Signals tab."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ad-studio/insights/as-insights-signals-create-ad.png",
          alt: "Creating an ad from a signal"
        })
      }), "\n", _jsx(_components.p, {
        children: "You can start multiple chats from different signals in parallel without breaking your workflow."
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Check the Signals tab regularly to catch timely opportunities. Signals update as new events emerge, so what's relevant today may shift quickly."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "act-on-insights",
        children: "Act on insights"
      }), "\n", _jsx(_components.p, {
        children: "From any Insights tab, you can create new ads based on what you find:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Insight"
            }), _jsx(_components.th, {
              children: "Action"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Top-performing ad identified"
            }), _jsx(_components.td, {
              children: "Create variants to scale what's working"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Fatiguing ad flagged"
            }), _jsx(_components.td, {
              children: "Generate fresh variants or try a new creative direction"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Competitor trend spotted"
            }), _jsx(_components.td, {
              children: "Riff on the competitor or ride the trend with your own creative"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Real-time signal discovered"
            }), _jsx(_components.td, {
              children: "Create an ad directly from the signal brief"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Performance gap in a product category"
            }), _jsx(_components.td, {
              children: "Create product-focused ads using your catalog"
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "whats-next",
        children: "What's next"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/ad-studio/ads",
            children: "Create ads: Start a chat and generate concepts"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/agents/reports",
            children: "Reports: Schedule recurring performance analyses and deliver to Slack or email"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
